// Contact form component
import { useForm, Controller } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { contactSchema, type ContactInput, type Account, type Address } from '../../../shared/types'
import { useTranslation } from '../../hooks/useTranslation'
import { Autocomplete, type AutocompleteOption } from '../ui/Autocomplete'
import { createCrudApi, type PaginatedResponse } from '../../lib/api'
import { OwnerSelect } from './OwnerSelect'

interface Props {
  defaultValues?: Partial<ContactInput>
  onSubmit: (data: ContactInput) => void
  onCancel?: () => void
  loading?: boolean
  accountId?: string
  accountName?: string
  canWriteField?: (field: string) => boolean
}

const accountsApi = createCrudApi<Account, unknown>('accounts')
const addressesApi = createCrudApi<Address, unknown>('addresses')

async function searchAccounts(search: string): Promise<AutocompleteOption[]> {
  const response: PaginatedResponse<Account> = await accountsApi.list({
    filters: { name: search },
    limit: 10,
  })
  return response.data.map((account) => ({
    id: account.id,
    label: account.name,
  }))
}

function formatAddress(address: Address): string {
  return `${address.street}, ${address.city}${address.state ? `, ${address.state}` : ''} ${address.postalCode || ''}, ${address.country}`
}

export function ContactForm({ defaultValues, onSubmit, onCancel, loading, accountId, accountName, canWriteField = () => true }: Props) {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<ContactInput>({
    resolver: zodResolver(contactSchema),
    defaultValues: accountId
      ? { useAccountAddress: true, ...defaultValues, accountId }
      : { useAccountAddress: true, ...defaultValues },
  })

  const selectedAccountId = watch('accountId') || accountId
  const useAccountAddress = watch('useAccountAddress')

  // Fetch addresses for the selected account
  const { data: addressesResponse } = useQuery({
    queryKey: ['addresses', 'byAccount', selectedAccountId],
    queryFn: () => addressesApi.list({ filters: { accountId: selectedAccountId! }, limit: 50 }),
    enabled: !!selectedAccountId,
  })

  const accountAddresses = addressesResponse?.data || []

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
            {t('fields.firstName')}
          </label>
          <input
            {...register('firstName')}
            type="text"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.firstName && (
            <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
            {t('fields.lastName')} <span className="text-red-500">*</span>
          </label>
          <input
            {...register('lastName')}
            type="text"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.lastName && (
            <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            {t('fields.email')}
          </label>
          <input
            {...register('email')}
            type="email"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
            {t('fields.phone')}
          </label>
          <input
            {...register('phone')}
            type="tel"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700">
          {t('fields.title')}
        </label>
        <input
          {...register('title')}
          type="text"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        />
        {errors.title && (
          <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
        )}
      </div>

      {!accountId && (
        <Controller
          name="accountId"
          control={control}
          render={({ field }) => (
            <Autocomplete
              value={field.value || ''}
              onChange={(id) => field.onChange(id)}
              fetchOptions={searchAccounts}
              label={t('fields.account')}
              placeholder="Type account name..."
              required
              error={errors.accountId?.message}
              minChars={2}
              queryKey="accounts"
              initialLabel={accountName}
            />
          )}
        />
      )}

      {/* Address Section */}
      {selectedAccountId && (
        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">{t('fields.mailingAddress')}</h4>

          <div className="flex items-center mb-3">
            <input
              type="checkbox"
              {...register('useAccountAddress')}
              id="useAccountAddress"
              className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            <label htmlFor="useAccountAddress" className="ml-2 text-sm text-gray-700">
              {t('fields.useAccountAddress')}
            </label>
          </div>

          {!useAccountAddress && (
            <div>
              <label htmlFor="mailingAddressId" className="block text-sm font-medium text-gray-700">
                {t('fields.selectAddress')}
              </label>
              <select
                {...register('mailingAddressId')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
              >
                <option value="">{t('common.select')}</option>
                {accountAddresses.map((address) => (
                  <option key={address.id} value={address.id}>
                    [{address.type}] {formatAddress(address)}
                  </option>
                ))}
              </select>
              {accountAddresses.length === 0 && (
                <p className="mt-1 text-sm text-gray-500">
                  No addresses found for this account. Add addresses on the account page first.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Owner assignment - only visible to admins/managers */}
      <Controller
        name="ownerId"
        control={control}
        render={({ field }) => (
          <OwnerSelect
            value={field.value}
            onChange={field.onChange}
            disabled={loading}
          />
        )}
      />

      <div className="flex justify-end gap-3 pt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {loading ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </form>
  )
}
